import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import ReferenceAutocomplete from '../ReferenceAutocomplete';
import { instanceValueChanged } from '../../utils/registryBindings';
import { MONO_FONT } from '../../constants/typography';
import {
  ROW_PATH_RE,
  isPlainObject,
  isScalar,
  formatCell,
  coerceCell,
  sameKeys,
  rowLabel,
  PAGINATION_LABELS,
  cellSx,
  collectMarkers,
  escapeAttributeSelectorValue
} from './BoundArrayTable.helpers';

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const EMPTY_INDEX = new Map();
const EMPTY_ROWS = [];

/**
 * Tabular editor for an array of objects whose leaves are bound to registries (§9.4).
 * Row identity: `rowKeys[position]` is the original index (matching server paths)
 * or `new-n` for rows added in this edit session.
 */
const BoundArrayTable = ({
  root,
  rows = EMPTY_ROWS,
  columns = [],
  instanceIndex = EMPTY_INDEX,
  editable = false,
  onChange,
  onlyInvalid = false,
  focusRequest,
  onModifiedCountChange,
}) => {
  const [rowKeys, setRowKeys] = useState(() => rows.map((_, i) => i));
  // Row count when identity was last reset: server markers beyond it are ignored
  // when counting deleted rows as "corrected".
  const [baseCount, setBaseCount] = useState(rows.length);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [activeCell, setActiveCell] = useState(null); // { rowKey, leaf }
  const [pendingFocus, setPendingFocus] = useState(null); // concrete path
  const containerRef = useRef(null);
  const newCounterRef = useRef(0);

  // Outside edit mode, or after an external change (re-fetch/reset), identity = position.
  // Our own add/delete update `rows` (via onChange) and `rowKeys` in the same batch,
  // so lengths match and identity is preserved.
  useEffect(() => {
    if (!editable) setActiveCell(null);
    if (editable && rowKeys.length === rows.length) return;
    const next = rows.map((_, i) => i);
    if (!sameKeys(rowKeys, next)) setRowKeys(next);
    if (baseCount !== rows.length) setBaseCount(rows.length);
    // Reacts to external row/mode changes only; rowKeys/baseCount are read as of this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, editable]);

  const markers = useMemo(() => collectMarkers(root, columns, instanceIndex), [root, columns, instanceIndex]);

  const keys = useMemo(() => {
    const union = [];
    rows.forEach((row) => {
      if (isPlainObject(row)) Object.keys(row).forEach((k) => { if (!union.includes(k)) union.push(k); });
    });
    columns.forEach((c) => { if (!union.includes(c.leaf)) union.push(c.leaf); });
    return union;
  }, [rows, columns]);

  const entries = rows.map((row, position) => ({ row, position, key: rowKeys[position] ?? position }));
  // Filter on the frozen server set (original indices) so rows don't vanish while
  // being fixed; rows added in this session always stay visible.
  const visible = onlyInvalid
    ? entries.filter((e) => typeof e.key !== 'number' || markers.has(e.key))
    : entries;
  const pageCount = Math.max(1, Math.ceil(visible.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageEntries = visible.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);

  const modifiedCount = useMemo(() => {
    let count = 0;
    markers.forEach((byLeaf, original) => {
      const position = rowKeys.indexOf(original);
      byLeaf.forEach((instance, leaf) => {
        if (position < 0) { // row deleted in this session (if it existed at load)
          if (original < baseCount) count += 1;
          return;
        }
        const row = rows[position];
        if (instanceValueChanged(isPlainObject(row) ? row[leaf] : undefined, instance)) count += 1;
      });
    });
    return count;
  }, [markers, rowKeys, rows, baseCount]);

  useEffect(() => {
    if (onModifiedCountChange) onModifiedCountChange(root, modifiedCount);
  }, [root, modifiedCount, onModifiedCountChange]);

  // Declared BEFORE the focusRequest effect: on a jump both change in the same
  // commit and the jump's page must win.
  useEffect(() => { setPage(0); }, [onlyInvalid]);

  useEffect(() => {
    if (!focusRequest?.path || !focusRequest.path.startsWith(`${root}[`)) return;
    const match = ROW_PATH_RE.exec(focusRequest.path.slice(root.length));
    if (!match) return;
    const target = Number(match[1]);
    const position = visible.findIndex((e) => e.key === target);
    if (position < 0) return;
    setPage(Math.floor(position / rowsPerPage));
    setPendingFocus(focusRequest.path);
    // Only a new request (nonce) triggers a jump; `visible` is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  useEffect(() => {
    if (!pendingFocus || !containerRef.current) return;
    const el = containerRef.current.querySelector(
      `[data-cell-path="${escapeAttributeSelectorValue(pendingFocus)}"]`
    );
    if (el) {
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.focus();
    }
    setPendingFocus(null);
  }, [pendingFocus, safePage]);

  const updateCell = (position, leaf, value) => {
    if (!onChange) return;
    onChange(rows.map((row, i) => (i === position ? { ...(isPlainObject(row) ? row : {}), [leaf]: value } : row)));
  };

  const deleteRow = (position) => {
    if (!onChange) return;
    onChange(rows.filter((_, i) => i !== position));
    setRowKeys((prev) => prev.filter((_, i) => i !== position));
    setActiveCell(null);
  };

  const addRow = () => {
    if (!onChange) return;
    newCounterRef.current += 1;
    const empty = keys.reduce((acc, k) => ({ ...acc, [k]: '' }), {});
    onChange([...rows, empty]);
    setRowKeys((prev) => [...prev, `new-${newCounterRef.current}`]);
    setPage(Math.floor(visible.length / rowsPerPage));
  };

  const activate = (key, leaf) => setActiveCell({ rowKey: key, leaf });

  const renderBoundCell = (entry, column) => {
    const { row, position, key } = entry;
    const current = isPlainObject(row) ? row[column.leaf] : undefined;
    const instance = typeof key === 'number' ? markers.get(key)?.get(column.leaf) : undefined;
    const modified = Boolean(instance) && instanceValueChanged(current, instance);
    const state = instance ? (modified ? 'modified' : 'invalid') : 'plain';
    const path = typeof key === 'number' ? `${root}[${key}].${column.leaf}` : undefined;
    const isActive = editable && activeCell?.rowKey === key && activeCell?.leaf === column.leaf;
    const label = `${column.leaf}, riga ${rowLabel(key)}`;

    let content;
    if (isActive) {
      content = (
        <ReferenceAutocomplete
          field={{ name: column.leaf, reference: { registryId: column.binding.registryId, field: column.binding.field } }}
          value={current === null || current === undefined ? '' : String(current)}
          onChange={(v) => { updateCell(position, column.leaf, v); setActiveCell(null); }}
          error={state === 'invalid' ? (instance.notes || true) : undefined}
          size="small"
          autoFocus
          openOnFocus
          hideLabel
        />
      );
    } else if (editable) {
      content = (
        <Box
          role="button"
          tabIndex={0}
          aria-label={label}
          data-cell-path={path}
          onClick={() => activate(key, column.leaf)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(key, column.leaf); }
          }}
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '0.8125rem',
            px: 1,
            py: 0.5,
            minHeight: 28,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { borderColor: 'text.primary' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
          }}
        >
          {formatCell(current)}
        </Box>
      );
    } else {
      content = (
        <Typography variant="body2" sx={{ fontFamily: MONO_FONT, fontSize: '0.8125rem' }}>
          {formatCell(current)}
        </Typography>
      );
    }

    return (
      <TableCell
        key={column.leaf}
        sx={cellSx(state)}
        data-cell-state={state}
        tabIndex={editable ? undefined : -1}
        data-cell-path={editable ? undefined : path}
      >
        {content}
        {state === 'invalid' && !isActive && instance.notes && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
            {instance.notes}
          </Typography>
        )}
        {state === 'modified' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'warning.dark' }}>
            modificato
          </Typography>
        )}
      </TableCell>
    );
  };

  const renderPlainCell = (entry, key) => {
    const value = isPlainObject(entry.row) ? entry.row[key] : undefined;
    if (editable && isScalar(value)) {
      return (
        <TableCell key={key}>
          <TextField
            size="small"
            variant="standard"
            fullWidth
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(e) => updateCell(entry.position, key, coerceCell(e.target.value, value))}
            inputProps={{ 'aria-label': `${key}, riga ${rowLabel(entry.key)}` }}
          />
        </TableCell>
      );
    }
    return (
      <TableCell key={key}>
        <Typography variant="body2" sx={{ fontFamily: isScalar(value) ? undefined : MONO_FONT }}>
          {formatCell(value)}
        </Typography>
      </TableCell>
    );
  };

  const colSpan = keys.length + 1 + (editable ? 1 : 0);

  return (
    <Paper variant="outlined" ref={containerRef}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 1.5, pt: 1.25, pb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontFamily: MONO_FONT }}>{root}</Typography>
        <Typography variant="caption" color="text.secondary">
          {rows.length === 1 ? '1 riga' : `${rows.length} righe`}
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small" aria-label={`Tabella ${root}`}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }} aria-label="Riga" />
              {keys.map((k) => {
                const column = columns.find((c) => c.leaf === k);
                return (
                  <TableCell key={k} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {column ? (
                      <Tooltip title={`${column.binding.registryId}.${column.binding.field}`}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <MenuBookIcon
                            titleAccess="Colonna collegata a un'anagrafica"
                            sx={{ fontSize: 14, color: 'primary.main' }}
                          />
                          {k}
                        </Box>
                      </Tooltip>
                    ) : k}
                  </TableCell>
                );
              })}
              {editable && <TableCell sx={{ width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageEntries.map((entry) => (
              <TableRow key={entry.key} hover>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ fontFamily: MONO_FONT, fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                  {typeof entry.key === 'number' ? `[${entry.key}]` : '[+]'}
                </TableCell>
                {keys.map((k) => {
                  const column = columns.find((c) => c.leaf === k);
                  return column ? renderBoundCell(entry, column) : renderPlainCell(entry, k);
                })}
                {editable && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Elimina riga ${rowLabel(entry.key)}`}
                      onClick={() => deleteRow(entry.position)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {pageEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="text.secondary">
                    {onlyInvalid ? 'Nessuna riga non valida' : 'Nessuna riga'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', px: 1 }}>
        {editable ? (
          <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Aggiungi riga</Button>
        ) : <span />}
        <TablePagination
          component="div"
          count={visible.length}
          page={safePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={(event, next) => setPage(next)}
          onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} di ${count}`}
          getItemAriaLabel={(type) => PAGINATION_LABELS[type]}
        />
      </Box>
    </Paper>
  );
};

export default BoundArrayTable;
